import { useState } from "react";
import type { ComputedMetrics } from "@/types/metrics";
import { Card } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

const GROUP_COLORS: Record<string, string> = {
  Exam: "var(--brand-dark-teal)",
  Exotic: "var(--brand-mid-teal)",
  Urgent: "var(--perf-red)",
  "Spay & Neuter": "var(--brand-green-dark)",
  Surgery: "var(--brand-green-mid)",
  Procedure: "var(--brand-lilac-dark)",
  Dental: "var(--brand-lilac-mid)",
  Other: "#9ca3af",
};

function groupColor(name: string): string {
  return GROUP_COLORS[name] ?? "#9ca3af";
}

interface AppointmentTypeBarProps {
  current: ComputedMetrics;
  typeFilterCount?: number;
}

export function AppointmentTypeBar({ current, typeFilterCount = 0 }: AppointmentTypeBarProps) {
  const [listOpen, setListOpen] = useState(false);
  const { appointmentGroupMix, appointmentMix } = current;
  if (appointmentGroupMix.length === 0) return null;

  return (
    <Card className="relative px-5 py-4 gap-0 shadow-none border-black/10 mb-7">
      {typeFilterCount > 0 && (
        <div className="text-[11px] text-gray-400 mb-2">
          Filtered to {typeFilterCount} appointment type{typeFilterCount === 1 ? "" : "s"} — shares
          are relative to the selected types
        </div>
      )}
      {/* Stacked bar — each segment opens a HoverCard with its slug breakdown */}
      <div className="flex rounded-md overflow-hidden h-9">
        {appointmentGroupMix.map((g) => {
          const pct = (g.pct * 100).toFixed(1);
          return (
            <HoverCard key={g.group} openDelay={100} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div
                  className="flex items-center justify-center text-[11px] font-medium text-white overflow-hidden shrink-0 cursor-default"
                  style={{ width: `${pct}%`, background: groupColor(g.group), minWidth: 0 }}
                >
                  {Number(pct) > 5 ? `${pct}%` : ""}
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                side="bottom"
                align="start"
                className="bg-white border-black/10 rounded-lg shadow-lg py-2 px-0 min-w-[200px] w-auto"
              >
                <div className="px-3 pb-1.5 mb-1 border-b border-black/5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                  {g.group}
                </div>
                <div className="max-h-[240px] overflow-y-auto">
                  {g.types.map((item) => (
                    <div key={item.group} className="flex justify-between items-center px-3 py-1">
                      <span className="text-[12px] text-gray-600">{item.label}</span>
                      <span className="text-[12px] text-gray-400 ml-6 tabular-nums">
                        {(item.pct * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-x-4 gap-y-1.5 mt-3 flex-wrap">
        {appointmentGroupMix.map((g) => (
          <div key={g.group} className="flex items-center gap-1.5 text-[12px] text-gray-500">
            <span
              className="w-2 h-2 rounded-[2px] shrink-0"
              style={{ background: groupColor(g.group) }}
            />
            {g.group}
            <span className="text-gray-400">{(g.pct * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>

      {/* Foldable detail list — slug level */}
      <div className="mt-3 border-t border-black/5 pt-2.5">
        <button
          onClick={() => setListOpen((o) => !o)}
          className="flex items-center w-full text-left gap-2 py-1 text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
        >
          <span
            className="text-[9px] text-gray-400 transition-transform duration-150 inline-block"
            style={{ transform: listOpen ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            ▶
          </span>
          <span className="font-medium">Appointment type</span>
          <span className="ml-auto text-gray-400 pr-1">%</span>
        </button>

        {listOpen && (
          <div className="mt-1">
            {appointmentMix.map((item) => (
              <div
                key={item.group}
                className="flex items-center justify-between py-1.5 border-b border-black/[0.04] last:border-0"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-[2px] shrink-0"
                    style={{
                      background: groupColor(
                        appointmentGroupMix.find((g) => g.types.some((t) => t.group === item.group))
                          ?.group ?? "Other"
                      ),
                    }}
                  />
                  <span className="text-[12px] text-gray-600">{item.label}</span>
                </div>
                <span className="text-[12px] text-gray-500 tabular-nums">
                  {(item.pct * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
